import React from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// JoinBounceModal (CC_POLITE_JOIN_BOUNCES, 2026-06-07) — the polite
// notice shown when a public-lobby tile click can't seat the player
// because the lobby filled, launched, or evaporated between the list
// render and the click. Mirrors LobbyDestroyedModal's tone + pattern
// (static backdrop, single dismiss button that lands the player back
// at the Main Menu); it does NOT invent a new notice system.
//
// Controlled, not self-listening (unlike LobbyDestroyedModal, which
// keys off a window broadcast): the join result is owned by GamePage's
// engagement effect, so GamePage drives this via the `code` prop and
// clears it via `onDismiss`. A null `code` renders nothing.

// Sentinel code for "join failed but no typed backend code is
// available" (transport failure, or a non-2xx with no/garbled JSON
// body). Maps to the generic notice copy below.
export const JOIN_BOUNCE_DEFAULT_CODE = '__default__';

// COPY PLACEHOLDER (CC_POLITE_JOIN_BOUNCES, 2026-06-07): MICHAEL OWNS
// THE FINAL WORDING. These are polite placeholders in the
// "Sorry, that game just filled up!" energy, flagged verbatim in the
// hand-back. Keys mirror the backend LobbyJoinError.code values
// (hsh_python/lobbies/services.py) so the map extends cleanly as the
// rail grows — e.g. once the live-games design lands, a PLAYING public
// tile becomes observable/joinable and `wrong_status` gets re-pointed
// at an "observe" affordance rather than a dead end.
const COPY = {
    lobby_full: {
        title: 'That game just filled up',
        body: 'Sorry — that game grabbed its last seat right before you '
            + 'got there. Let us find you another one.',
    },
    wrong_status: {
        title: 'That game already started',
        body: 'Sorry — that game has already kicked off, so there is no '
            + 'seat to grab right now. Watching games in progress is '
            + 'coming soon.',
    },
    not_found: {
        title: 'That game is gone',
        body: 'Sorry — that game wrapped up or closed just before you '
            + 'clicked. Let us get you back to the list.',
    },
    // Defensive: reachable today from an open-web public tile when the
    // clicker holds no seat and the lobby is bound under the owner's key
    // (hsh_python/lobbies/services.py join_lobby_by_uuid). The public
    // cross-platform join rail is CC_CROSS_PLATFORM_PLAY's territory;
    // this entry just keeps the notice polite if it surfaces here.
    lobby_not_bound_to_channel: {
        title: 'Could not join that game',
        body: 'Sorry — we could not get you into that game from here. '
            + 'Let us head back and try another.',
    },
    [JOIN_BOUNCE_DEFAULT_CODE]: {
        title: 'Could not join that game',
        body: 'Sorry — something got in the way of joining that game. '
            + 'Let us head back and try again.',
    },
};

// COPY PLACEHOLDER: the dismiss button label. Michael owns the final.
const DISMISS_LABEL = 'Back to games';


export default function JoinBounceModal({ code, onDismiss }) {
    const show = code != null;
    const copy = COPY[code] || COPY[JOIN_BOUNCE_DEFAULT_CODE];
    return (
        <Modal show={show}
               onHide={onDismiss}
               backdrop="static"
               keyboard={false}
               centered>
            <Modal.Header>
                <Modal.Title>{copy.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div
                    data-testid="join-bounce-notice"
                    data-code={code || ''}
                >
                    {copy.body}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="primary"
                    data-testid="join-bounce-dismiss"
                    onClick={onDismiss}
                >
                    {DISMISS_LABEL}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
